import React, { useEffect, useState } from 'react';
import { CompletionStatus, FetchType } from '../../../../enums';
import { OrganizationLevelType } from '../../../../enums/organization-level';
import { KeplerState } from '../../../../models/kepler-state';
import { useAppActions, useAppState } from '../../../../overmind';
import { ContinueLearningWidget } from '../../../../widgets/continue-learning/continue-learning';
import { AdditionalLearningPanel } from '../../../learning-journey/additional-learning/panel/additional-learning.panel';
import { AdditionalLearningViewModel } from '../../../learning-journey/additional-learning/additional-learning.models';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';
import { ReflectionPanel } from '../../../learning-journey/reflections/reflection.panel';
import { IPersonLayoutProps } from './person.models';
import { PagePath } from '../../../../navigation/navigation.enums';
import { PermissionsEntity } from '../../../../widgets/analytics/permissions-entity-dropdown/permissions-entity-dropdown.models';
import { PersonDetails } from './person-details/person-details';
import { ActionCard, PageHeader, CascadingPanels, useCascadingPanelsConnector, CascadingPanel } from '@keplerco/core';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { EntityAverageWidget } from '../../../../components/general/average/entity-average/entity-average.widget';
import { DailyLoginsWidget, SkillsBreakdownWidget, SkillScoreOverTimeWidget } from '../../../../widgets';
import { CourseEngagementWidget } from '../../../../widgets/analytics/overview/course-engagement.widget';
import analyticsStyles from '../../analytics.module.css';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { SortByOption } from '../../../../enums/sort-by-option';
import { MAX_PAGE_SIZE } from '../../../../library/consts/consts';

enum CascadingPanelIds {
  AdditionalLearning = 'AdditionalLearning',
  Reflection = 'Reflection',
}

export function PersonLayout(props: IPersonLayoutProps): JSX.Element {
  const { isLoading, path, slug, firstName, lastName, email, department, jobTitle, keplerPoints, lessons, breadcrumbs } = props;

  const actions = useAppActions();
  const { user, companyVariables } = useAppState<KeplerState>();

  const keplerNavigate = useKeplerNavigate();

  const { openPanelIds, next, close, closeAll } = useCascadingPanelsConnector();

  const [entity, setEntity] = useState<PermissionsEntity>({ organizationLevel: OrganizationLevelType.Learner, entity: { slug: slug, name: `${firstName} ${lastName}` } });
  const [pendingLearnerAssessmentsCount, setPendingLearnerAssessmentsCount] = useState<number>(0);
  const [selectedReflectionId, setSelectedReflectionId] = useState<number>();

  useEffect(() => {
    async function getData() {
      if (path !== PagePath.dashboard) return;
      const result = await actions.getLearnerAssessments({
        organizationLevel: OrganizationLevelType.Learner,
        companySlug: companyVariables.slug,
        departmentSlug: undefined,
        teamSlug: undefined,
        learnerSlug: user?.learnerSlug,
        search: undefined,
        sortBy: SortByOption.MostRecentAssigned,
        pageSize: MAX_PAGE_SIZE,
        page: 1,
        isArchived: false,
      });
      if (!!result) setPendingLearnerAssessmentsCount(result.items.filter(value => value.isActive && value.completionStatus !== CompletionStatus.Completed).length);
    }
    getData();
  }, []);

  useEffect(() => {
    if (isLoading) return;
    setEntity({ organizationLevel: OrganizationLevelType.Learner, entity: { slug: slug, name: `${firstName} ${lastName}` } });
  }, [isLoading, slug, firstName]);

  // additional learning
  async function onSubmitAdditionalLearningHandler(model: AdditionalLearningViewModel, reflect?: boolean) {
    actions.startLoader({ path: path, type: FetchType.Sending });

    const reflectionId = await actions.saveAdditionalLearning(model);

    if (!reflectionId) {
      closeAll();
      actions.stopLoader(path);
      return;
    }

    actions.addNotification({
      title: 'Additional Learning Captured',
      message: `Your Additional Learning - ${model.name} - has been captured.`,
      priority: NotificationPriority.Toast,
      type: NotificationType.Success,
      id: `additional_learning_${Date.now() + Math.random()}`,
      active: true,
    });

    if (reflect) {
      setSelectedReflectionId(reflectionId);
      next(CascadingPanelIds.Reflection);
      actions.stopLoader(path);
      return;
    }

    closeAll();
    actions.stopLoader(path);
  }

  // reflection
  function onCloseReflectionHandler() {
    setSelectedReflectionId(undefined);
    closeAll();
  }

  return (
    <React.Fragment>
      <div id={path === PagePath.dashboard ? 'dashboard-page' : 'person-page'} className="pageWrapper wrapper">
        <SkeletonOverlay isLoading={isLoading}>
          <div className={analyticsStyles.page}>
            {path !== PagePath.dashboard && (
              <React.Fragment>
                <PageHeader breadcrumbs={breadcrumbs} divider />

                <br />
              </React.Fragment>
            )}

            <PersonDetails path={path} slug={slug} firstName={firstName} lastName={lastName} jobTitle={jobTitle} department={department} email={email} keplerPoints={keplerPoints} />

            {!!pendingLearnerAssessmentsCount && (
              <ActionCard
                icon="/assessments-pending.svg"
                title={`Assessment${pendingLearnerAssessmentsCount === 1 ? '' : `s`} pending`}
                description={`You currently have ${pendingLearnerAssessmentsCount} assessments pending`}
                action={{
                  type: `anchor`,
                  text: `View Assessments`,
                  onClick: () => keplerNavigate(PagePath.yourAssessmentsBase),
                }}
              />
            )}

            <div className={analyticsStyles.oneTwoGrid}>
              <div id="entity-average-widget-section">
                <EntityAverageWidget path={path} entity={entity} />
              </div>

              <div id="skills-breakdown-section">
                <SkillsBreakdownWidget description={path === PagePath.dashboard ? `A breakdown of your skills and the corresponding skill scores` : `A breakdown of the skills for ${firstName} ${lastName} and the corresponding skill scores`} path={path} entity={entity} />
              </div>
            </div>

            <DailyLoginsWidget path={path} entity={entity} />

            <CourseEngagementWidget entity={entity} isLearner={path === PagePath.analyticsPerson} />

            <SkillScoreOverTimeWidget path={path} entity={entity} />

            {!!lessons && user?.allowOffPlatformLearning && (
              <div className="card">
                <ContinueLearningWidget lessons={lessons} onClickCaptureAdditionalLearning={() => next(CascadingPanelIds.AdditionalLearning)} />
              </div>
            )}
          </div>
        </SkeletonOverlay>
      </div>

      {/* TODO: unify additional learning and reflection panel combinations into a component */}
      {path !== PagePath.analyticsPerson && (
        <CascadingPanels
          openPanelIds={openPanelIds}
          onClosePanel={id => {
            if (id === CascadingPanelIds.Reflection) {
              onCloseReflectionHandler();
            }

            close(id);
          }}
        >
          <CascadingPanel id={CascadingPanelIds.AdditionalLearning}>
            <AdditionalLearningPanel path={path} onCancel={closeAll} onSubmit={onSubmitAdditionalLearningHandler} />
          </CascadingPanel>

          <CascadingPanel id={CascadingPanelIds.Reflection}>
            <ReflectionPanel reflectionId={selectedReflectionId} path={path} onCancel={onCloseReflectionHandler} onSubmit={onCloseReflectionHandler} />
          </CascadingPanel>
        </CascadingPanels>
      )}
    </React.Fragment>
  );
}
